import {
  call,
  put,
  takeLatest,
  all,
  takeEvery,
  take,
} from "redux-saga/effects";
import { eventChannel, END } from "redux-saga";
import _throttle from "lodash-es/throttle";
import axios from "axios";
import FetchBlob from "./fetch-blob";

import {
  UPLOAD_PICTURE_REQUESTING,
  UPLOAD_PICTURE_SUCCESS,
  UPLOAD_PICTURE_ERROR,
  SEARCH_UNSPLASH_ERROR,
  SEARCH_UNSPLASH_REQUESTING,
  SEARCH_UNSPLASH_SUCCESS,
  GET_RANDOM_UNSPLASH_REQUESTING,
  GET_RANDOM_UNSPLASH_SUCCESS,
  GET_RANDOM_UNSPLASH_ERROR,
  UPLOAD_FILE_REQUESTING,
  UPLOAD_FILE_SUCCESS,
  UPLOAD_FILE_ERROR,
  DOWNLOAD_FILE_SUCCESS,
  DOWNLOAD_FILE_ERROR,
  DOWNLOAD_FILE_REQUESTING,
  UPLOAD_FILE_PREVIEW_SUCCESS,
  UPLOAD_FILE_PREVIEW_ERROR,
} from "./constants";

import { fetchApi } from "../api";
import { changeUploadProgress } from "./actions";
import slugify from "slugify";
import { Platform } from "react-native";

const endPoints = {
  getUploadUrlS3: "/v1/upload/sign-s3",
  getUploadUrl: "/v1/upload/get-upload-url",
  downloadFile: "/v1/upload/download", // get
  searchUnsplash: "/v1/unsplash/search",
  randomUnsplash: "/v1/unsplash/random",
};

const upload = async ({
  file,
  signedRequest,
  fileType = "image/jpg",
  fileName = "",
}) => {
  return eventChannel(emitter => {
    const onProgress = (sent, total) => {
      const progress = sent / total;
      emitter({ progress, fileName });
    };

    let fileBinary = file;
    if (Platform.OS !== "web") {
      fileBinary = FetchBlob.wrap(decodeURIComponent(file));
    }

    const req = FetchBlob.fetch({
      httpMethod: "PUT",
      signedRequest,
      fileType,
      file: fileBinary,
      onProgress,
    });

    req.then(
      res => {
        emitter({ result: res.respInfo || res.body });
        emitter(END);
      },
      err => {
        emitter({ error: err });
        emitter(END);
      },
    );

    return () => {};
  });
};

const uploadS3WithoutProgress = async ({ file, signedRequest }) => {
  const options = {
    method: "PUT",
    body: file,
  };
  const response = await fetch(signedRequest, options);
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
  return response;
};

interface GetUploadUrlApiProps {
  urlName: string;
  fileType?: string;
  modelToUpdate?: string;
  modelIdToUpdate?: string;
  fileUrl?: string;
  filePreview?: string;
  data?: {};
}

const getUploadUrlApi = async ({
  urlName,
  fileType = "image/jpg",
  modelToUpdate = "stepFeedback",
  modelIdToUpdate = "",
  fileUrl = "",
  filePreview = "",
  data = {},
}: GetUploadUrlApiProps) => {
  return await fetchApi(
    `${endPoints.getUploadUrl}`,
    {
      fileType,
      fileName: urlName,
      modelToUpdate,
      modelIdToUpdate,
      fileUrl,
      filePreview,
      data,
    },
    "post",
  );
};

export const turnBlobUrlIntoFile = async blobUrl => {
  return await fetch(blobUrl).then(r => r.blob());
};

const downloadFileApi = async ({ fileName, fileType, urlName }) => {
  const s3Url = await fetchApi(
    `${endPoints.downloadFile}?file-name=${fileName}&file-extension=${fileType}&url-name=${urlName}`,
    {},
    "get",
  );

  const res = await axios.get(s3Url, {
    headers: { Accept: "*/*" },
  });

  window.open(s3Url);
};

const searchUnsplashApi = async payload => {
  return await fetchApi(endPoints.searchUnsplash, payload, "post");
};

const getRandomUnsplashApi = async payload => {
  return await fetchApi(endPoints.randomUnsplash, payload, "post");
};

export const uploadPictureWithoutRedux = async ({
  file,
  urlName,
  fileType,
}) => {
  try {
    const resData = await getUploadUrlApi({ urlName, fileType });

    const { signedRequest, url } = resData;
    const res = await uploadS3WithoutProgress({ signedRequest, file });
    return url;
  } catch (error) {
    console.log(error);
  }
};

function* progressListener(channel) {
  while (true) {
    const { progress, result, error, fileName = "" } = yield take(channel);
    if (progress) {
      yield put(changeUploadProgress({ progress: progress, fileName }));
    }

    if (result) return result;

    if (error) {
      throw error;
    }
  }
}

function* uploadPictureFlow(action) {
  try {
    const { file, urlName } = action.payload;

    const resData = yield call(getUploadUrlApi, { urlName });

    const { signedRequest, url } = resData;

    const channel = yield call(upload, { file, signedRequest });

    const result = yield progressListener(channel);

    yield put({ type: UPLOAD_PICTURE_SUCCESS, payload: url });
  } catch (error) {
    yield put({ type: UPLOAD_PICTURE_ERROR, payload: error });
  }
}

export function* uploadFileFlow(action) {
  try {
    const {
      file,
      urlName,
      fileType,
      fileName,
      preview = null,
      data = {},
      modelToUpdate = "stepFeedback",
    } = action.payload;

    let formattedFile = file;

    if (Platform.OS === "ios") {
      formattedFile = file?.replace("file://", "");
    } else if (Platform.OS === "web") {
      formattedFile =
        file instanceof File ? file : yield call(turnBlobUrlIntoFile, file);
    }

    const resData = yield call(getUploadUrlApi, {
      urlName,
      fileType,
      modelToUpdate,
      modelIdToUpdate: "",
      fileUrl: formattedFile,
      filePreview: preview,
      data,
    });
    const { signedRequest, passthrough = undefined, url } = resData;

    const channel = yield call(upload, {
      file: formattedFile,
      signedRequest,
      fileName,
      fileType,
    });

    const result = yield progressListener(channel);

    return yield put({
      type: UPLOAD_FILE_SUCCESS,
      payload: {
        ...action.payload,
        fileUrl: url || formattedFile,
        passthrough,
        muxVideoId: passthrough,
        fileName,
        fileType,
        uri: url || formattedFile,
        filePreview: preview || "",
      },
    });
  } catch (error) {
    console.log(error);
    yield put({ type: UPLOAD_FILE_ERROR, payload: error });
  }
}

export function* uploadFilePreviewFlow(action) {
  try {
    const { file, urlName, fileType, fileName } = action.payload;
    const resData = yield call(getUploadUrlApi, { urlName, fileType });

    const { signedRequest, url } = resData;

    const channel = yield call(upload, {
      file,
      signedRequest,
      fileName,
      fileType,
    });

    const result = yield progressListener(channel);

    return yield put({
      type: UPLOAD_FILE_PREVIEW_SUCCESS,
      payload: { ...action.payload, filePreview: url, fileName, fileType },
    });
  } catch (error) {
    yield put({ type: UPLOAD_FILE_PREVIEW_ERROR, payload: error });
  }
}

function* searchUnsplashFlow(action) {
  try {
    const result = yield call(searchUnsplashApi, action.payload);

    yield put({ type: SEARCH_UNSPLASH_SUCCESS, payload: result });
  } catch (error) {
    yield put({ type: SEARCH_UNSPLASH_ERROR, payload: error.message });
  }
}

function* getRandomUnsplashFlow(action) {
  try {
    const result = yield call(getRandomUnsplashApi, action.payload);

    yield put({ type: GET_RANDOM_UNSPLASH_SUCCESS, payload: result });
  } catch (error) {
    yield put({ type: GET_RANDOM_UNSPLASH_ERROR, payload: error.message });
  }
}

function* downloadFileFlow(action) {
  try {
    const { fileType, fileName, urlName } = action.payload;
    const s3Url = yield call(downloadFileApi, {
      fileName: slugify(fileName),
      fileType,
      urlName,
    });

    // downloadImage
    yield put({ type: DOWNLOAD_FILE_SUCCESS, payload: {} });
  } catch (error) {
    console.log(error);
    yield put({ type: DOWNLOAD_FILE_ERROR, payload: error?.message });
  }
}

function* Saga() {
  yield all([
    takeLatest(UPLOAD_PICTURE_REQUESTING, uploadPictureFlow),
    takeLatest(SEARCH_UNSPLASH_REQUESTING, searchUnsplashFlow),
    takeLatest(GET_RANDOM_UNSPLASH_REQUESTING, getRandomUnsplashFlow),
    takeEvery(UPLOAD_FILE_REQUESTING, uploadFileFlow),
    takeEvery(DOWNLOAD_FILE_REQUESTING, downloadFileFlow),
  ]);
}

export default Saga;
